/** @jsxImportSource @emotion/react */
import { BrowserRouter, Route, Switch } from 'react-router-dom'
import MainPage from 'components/pages/MainPage'
import { Notification } from 'components/partials/Notification'
import PrivateRoute from 'components/partials/PrivateRoute'
import LoginPage from 'components/pages/LoginPage'
import NotAuthorizedPage from 'components/pages/NotAuthorizedPage'
import LandingPageSideImage from 'components/partials/LandingPageSideImage'
import {
  DASHBOARD_PATH,
  MOBILE_APP_PATH,
  NOT_AUTHORIZED_PATH,
  EMAIL_ALERTS_CONFIRMATION,
  ACCOUNT_SUSPENDED_PATH,
  ORG_ID_PARAM,
  SELECTED_ORG_ID_LOCAL_STORAGE_KEY,
} from 'lib/constants'
import { StylesProvider } from '@material-ui/core/styles'
import { Provider } from 'hooks-for-redux'
import { responsiveLoginWrapperStyles } from 'styles'
import * as Models from 'models'
import MobileApp from 'components/pages/MobileApp'
import { useTranslation } from 'react-i18next'
import './App.css'
import EmailAlertsConfirmation from 'components/pages/EmailAlertsConfirmation'
import { GlobalProvider } from 'contexts/GlobalProvider'
import { ConnectivityOverlay } from 'components/partials/ConnectivityOverlay'
import { ChannelsDataProvider } from 'contexts/ChannelsDataProvider'
import { VoiceProvider } from 'contexts/VoiceProvider'
import AccountSuspendedPage from 'components/pages/AccountSuspendedPage'
// import BottomPTTBar from 'components/partials/BottomPTTBar'

let _global: any = window
_global.Models = Models

// Deeplink support: external apps link to `${portalUrl}/dashboard/main?orgId=<id>`.
// Capture the org id before routing — PrivateRoute drops the query string when it
// redirects unauthenticated users to login — so the selection survives the login
// round-trip via the same localStorage key GlobalProvider hydrates from.
const captureOrgIdDeeplink = () => {
  const params = new URLSearchParams(window.location.search)
  const orgId = params.get(ORG_ID_PARAM)
  if (!orgId) return
  localStorage.setItem(SELECTED_ORG_ID_LOCAL_STORAGE_KEY, orgId)
  params.delete(ORG_ID_PARAM)
  const search = params.toString()
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`,
  )
}
captureOrgIdDeeplink()

function App() {
  window._env_ ? console.log(`REACT_APP_ENV: `, window._env_.REACT_APP_ENV) : console.log(`No window._env_`)
  const { ready } = useTranslation('translations', { useSuspense: false })
  return (
    <>
      {ready && (
        <Provider>
          <StylesProvider injectFirst>
            <Notification />
            <BrowserRouter>
              <Switch>
                <Route exact path="/">
                  <div css={responsiveLoginWrapperStyles}>
                    <LandingPageSideImage />
                    <LoginPage />
                  </div>
                </Route>
                <Route path={`${MOBILE_APP_PATH}/:section?`}>
                  <div css={responsiveLoginWrapperStyles}>
                    <LandingPageSideImage />
                    <MobileApp />
                  </div>
                </Route>
                <Route path={NOT_AUTHORIZED_PATH}>
                  <div css={responsiveLoginWrapperStyles}>
                    <LandingPageSideImage />
                    <NotAuthorizedPage />
                  </div>
                </Route>
                <Route path={ACCOUNT_SUSPENDED_PATH}>
                  <div css={responsiveLoginWrapperStyles}>
                    <LandingPageSideImage />
                    <AccountSuspendedPage />
                  </div>
                </Route>
                <Route path={EMAIL_ALERTS_CONFIRMATION}>
                  <div css={responsiveLoginWrapperStyles}>
                    <LandingPageSideImage />
                    <EmailAlertsConfirmation />
                  </div>
                </Route>
                <GlobalProvider>
                  <ChannelsDataProvider>
                    <VoiceProvider>
                      {/* Inside the providers, so the checks only run for a signed-in
                          operator and the overlay can read the selected org. Mounted
                          here rather than in MainPage so switching dashboard sections
                          does not remount it and re-probe. */}
                      <ConnectivityOverlay />
                      <PrivateRoute path={DASHBOARD_PATH} component={MainPage} />
                    </VoiceProvider>
                  </ChannelsDataProvider>
                </GlobalProvider>
              </Switch>
            </BrowserRouter>
          </StylesProvider>
        </Provider>
      )}
    </>
  )
}

export default App
